import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import SkipToMain from '../components/skip-to-main';
import { Toaster } from '../components/ui/toaster';
import { AppSidebar } from '../components/layout/app-sidebar';
import { SidebarInset } from '../components/ui/sidebar';
import { cn } from '../lib/cn';
import { AssignmentsProvider } from '../features/assignments/context/assignments-context';
import { ErrorBoundary } from '../components/ui/error-boundary';
import { UsersDialogs } from '../features/users/components/users-dialogs';
import { lazyWithRetry } from '../lib/lazy-with-retry';

// Lazy load Page Components with automatic retry on chunk loading failures
const CompaniesPage = lazyWithRetry(() => import('../features/companies/index'));
const RealUsersPage = lazyWithRetry(() => import('../features/users/index'));
const ProjectListPageMobile = lazyWithRetry(() => import('../features/assignments/ProjectListPageMobile'));
const ProjectDetailPageMobile = lazyWithRetry(() => import('../features/assignments/pages/project-detail-mobile/ProjectDetailPageMobile'));
const SubprojectDetailPageMobile = lazyWithRetry(() => import('../features/assignments/pages/subproject-detail-mobile/SubprojectDetailPageMobile').then(module => ({ default: module.SubprojectDetailPageMobile })));
const TrucksPage = lazyWithRetry(() => import('../features/trucks/TrucksPage'));
const DailyReportsPage = lazyWithRetry(() => import('../features/reports/DailyReportsPage'));
const ItemsPage = lazyWithRetry(() => import('../features/items/ItemsPage'));
const AdminPermissionsPage = lazyWithRetry(() => import('../features/admin/AdminPermissionsPage'));
const AdminLanguagesPage = lazyWithRetry(() => import('../features/admin/AdminLanguagesPage'));
const AdminTranslationsPage = lazyWithRetry(() => import('../features/admin/AdminTranslationsPage'));
const PerformanceDashboard = lazyWithRetry(() => import('../features/admin/performance/PerformanceDashboard').then(module => ({ default: module.PerformanceDashboard })));
const ErrorLogsDashboard = lazyWithRetry(() => import('../features/admin/pages/ErrorLogsDashboard').then(module => ({ default: module.ErrorLogsDashboard })));
const TranslationMonitoring = lazyWithRetry(() => import('../features/admin/components/TranslationMonitoring').then(module => ({ default: module.TranslationMonitoring })));
const MailStatsPage = lazyWithRetry(() => import('../features/admin/components/MailStatsPage').then(module => ({ default: module.MailStatsPage })));

// Placeholders for other pages - replace with actual imports
const TasksPage = () => <div>Tasks Page Content</div>;
const AppsPage = () => <div>Apps Page Content</div>;
const ChatsPage = () => <div>Chats Page Content</div>;
const SettingsPage = () => <div>Settings Page Content</div>; // For /settings and its children
const AccountSettingsPage = () => <div>Account Settings Page</div>;
const AppearanceSettingsPage = () => <div>Appearance Settings Page</div>;
const NotificationsSettingsPage = () => <div>Notifications Settings Page</div>;
const DisplaySettingsPage = () => <div>Display Settings Page</div>;
const HelpCenterPage = () => <div>Help Center Page</div>;

// This is the refactored Main component, now acting as the main layout for authenticated content.
// It will be rendered by a ProtectedRoute in App.tsx.
// It needs to render an <Outlet /> for its child routes.
const MainLayout: React.FC = () => {
  // isAuthPage logic can likely be removed if MainLayout is always protected
  // const isAuthPage = useMemo(() => { ... }, [location.pathname]);

  return (
    <>
      <SkipToMain />
      {/* {!isAuthPage && <AppSidebar />} */}
      <AppSidebar /> {/* Assuming sidebar is always shown in MainLayout */}
      <SidebarInset
        id="main-content"
        className={cn(
          'min-h-screen bg-background font-sans antialiased',
          'transition-all duration-300 ease-in-out'
        )}
      >
        <ErrorBoundary>
          <AssignmentsProvider>
            {/* Define nested routes for the main layout */}
            <Suspense fallback={<div className="flex items-center justify-center h-64">Loading...</div>}>
            <Routes>
            <Route path="/" element={<Navigate to="/projects" replace />} />
            <Route path="/projects" element={<ProjectListPageMobile />} />
            <Route path="/project/:projectId" element={<ProjectDetailPageMobile />} />
            <Route path="/subproject/:subprojectId" element={<SubprojectDetailPageMobile />} />
            <Route path="/companies" element={<CompaniesPage />} />
            <Route path="/users" element={<RealUsersPage />} />
            <Route path="/trucks" element={<TrucksPage />} />
            <Route path="/daily-reports" element={<DailyReportsPage />} />
            <Route path="/items" element={<ItemsPage />} />
            <Route path="/tasks" element={<TasksPage />} />
            <Route path="/apps" element={<AppsPage />} />
            <Route path="/chats" element={<ChatsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/settings/account" element={<AccountSettingsPage />} />
            <Route path="/settings/appearance" element={<AppearanceSettingsPage />} />
            <Route path="/settings/notifications" element={<NotificationsSettingsPage />} />
            <Route path="/settings/display" element={<DisplaySettingsPage />} />
            <Route path="/help" element={<HelpCenterPage />} />
            <Route path="/admin/permissions" element={<AdminPermissionsPage />} />
            <Route path="/admin/languages" element={<AdminLanguagesPage />} />
            <Route path="/admin/translations" element={<AdminTranslationsPage />} />
            <Route path="/admin/performance" element={<PerformanceDashboard />} />
            <Route path="/admin/error-logs" element={<ErrorLogsDashboard />} />
            <Route path="/admin/translation-monitoring" element={<TranslationMonitoring />} />
            <Route path="/admin/mail-stats" element={<MailStatsPage />} />
            </Routes>
          </Suspense>
          </AssignmentsProvider>
        </ErrorBoundary>
      </SidebarInset>
      <Toaster />
      <UsersDialogs />
    </>
  );
};

export default MainLayout;